// PATH: src/pages/StudentDetailPage.tsx — fiche élève centralisée (onglets)
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Avatar, Button, Card, Col, DatePicker, Descriptions, Divider, Form, Input, List, Modal, Popconfirm,
  Row, Select, Space, Switch, Table, Tabs, Tag, Typography, message,
} from "antd";
import { PlusOutlined, PrinterOutlined, UserOutlined, WhatsAppOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { useSchoolApi } from "../api/school";
import { WHATSAPP_ENABLED } from "../config/features";
import { useCanEditStudents } from "../hooks/useCanEditStudents";
import { adminJustification, usePedagogyApi, type AttendanceRecord, type Bulletin } from "../api/pedagogy";
import { usePaymentsApi, type PaymentRow } from "../api/payments";
import PhotoUpload from "../components/PhotoUpload";
import DocumentUpload from "../components/DocumentUpload";
import { useTabParam } from "../hooks/useTabParam";
import { pushRecentStudent } from "../hooks/useRecentStudents";
import ResponsiveTable from "../components/ResponsiveTable";
import type {
  DisciplinaryRecord, Enrollment, Guardian, SchoolClass, SchoolYear, Student, StudentProfile,
} from "../models/school";

const enrollmentStatusColor: Record<string, string> = {
  EN_ATTENTE: "orange",
  VALIDEE: "green",
  ANNULEE: "default",
};

const StudentDetailPage: React.FC = () => {
  const { id } = useParams();
  const studentId = Number(id);
  const api = useSchoolApi();
  const pedagogy = usePedagogyApi();
  const payApi = usePaymentsApi();
  const navigate = useNavigate();

  const [tab, setTab] = useTabParam("identite");
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  // Vue 360° : assiduité, notes et paiements de l'élève, chargés à part du
  // profil (endpoints distincts, gardes distinctes). Les paiements sont
  // réservés à la direction : un 403 masque l'onglet au lieu de bloquer.
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [payments, setPayments] = useState<PaymentRow[] | null>(null);
  const [bulletins, setBulletins] = useState<Bulletin[]>([]);
  const [years, setYears] = useState<SchoolYear[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  // Inscription dont on change la classe. État distinct de `classes` ci-dessus,
  // que la modale d'inscription pilote déjà : les deux modales portent sur des
  // années différentes et se disputeraient la même liste.
  const [classEnrollment, setClassEnrollment] = useState<Enrollment | null>(null);
  const [guardianModalOpen, setGuardianModalOpen] = useState(false);
  // Tuteur en cours de modification ; non nul ⇒ modale ouverte.
  const [editingGuardian, setEditingGuardian] = useState<Guardian | null>(null);
  // Écriture du dossier : direction ou surveillance générale. Un enseignant
  // consulte la fiche entière mais n'y voit aucune action (gate backend
  // `ecritureEleve`, qui refuserait de toute façon).
  const { canEdit } = useCanEditStudents();
  const [disciplineModalOpen, setDisciplineModalOpen] = useState(false);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);

  const load = useCallback(() => {
    api
      .getStudent(studentId)
      .then((p) => {
        setProfile(p);
        // Alimente « derniers dossiers consultés » (palette de recherche,
        // tableau de bord) — commodité locale au poste.
        pushRecentStudent({ id: p.student.id, name: `${p.student.last_name} ${p.student.first_name}` });
      })
      .catch(() => message.error("Fiche élève introuvable"));
  }, [api, studentId]);

  useEffect(load, [load]);
  useEffect(() => {
    api.listYears().then(setYears).catch(() => undefined);
  }, [api]);

  // loadAttendance est réutilisé après une justification, pour rafraîchir la
  // ligne sans recharger toute la fiche.
  const loadAttendance = useCallback(() => {
    pedagogy.listAttendanceByStudent(studentId).then(setAttendance).catch(() => setAttendance([]));
  }, [pedagogy, studentId]);
  useEffect(loadAttendance, [loadAttendance]);

  useEffect(() => {
    // null = onglet masqué (403 hors direction) ; [] = aucun pointage.
    payApi.listByStudent(studentId).then(setPayments).catch(() => setPayments(null));
  }, [payApi, studentId]);

  // Bulletins : un par trimestre de l'année de la dernière inscription. Les
  // trimestres sont propres à une année, d'où la dépendance au profil.
  const latestYearId = profile?.enrollments?.[0]?.school_year_id;
  useEffect(() => {
    if (!latestYearId) { setBulletins([]); return; }
    let cancelled = false;
    pedagogy.listTerms(latestYearId)
      .then((terms) =>
        Promise.all(terms.map((t) => pedagogy.getBulletin(studentId, t.id).catch(() => null)))
      )
      .then((rs) => { if (!cancelled) setBulletins(rs.filter((b): b is Bulletin => b != null)); })
      .catch(() => { if (!cancelled) setBulletins([]); });
    return () => { cancelled = true; };
  }, [pedagogy, studentId, latestYearId]);

  if (!profile) {
    return <Card loading />;
  }
  const { student } = profile;

  const printCertificate = (enrollment: Enrollment, docType: "CERTIFICAT_SCOLARITE" | "ATTESTATION_INSCRIPTION") => {
    navigate(`/enrollments/${enrollment.id}/print?doc_type=${docType}`);
  };

  // ── Synthèse : ce qu'on veut savoir d'un élève sans ouvrir un onglet ───────
  const monthStart = dayjs().startOf("month");
  const absencesThisMonth = attendance.filter(
    (r) => r.kind === "ABSENCE" && dayjs(r.event_date).isAfter(monthStart)
  ).length;
  // Moyenne du dernier bulletin disponible (le plus récent trimestre noté).
  const lastAverage = bulletins.filter((b) => b.overall_average != null).slice(-1)[0]?.overall_average;
  // Reste dû : mêmes règles que l'écran Paiements — montant figé au pointage,
  // sinon tarif courant ; seul ce qui n'est PAS payé compte.
  const outstanding = (payments ?? []).reduce((sum, p) => {
    const amount = p.amount_due ?? p.amount_current;
    return p.status !== "PAYE" && amount != null ? sum + amount : sum;
  }, 0);

  return (
    <div>
      {/* Identité et actions séparées : sur écran étroit (classe `page-header`,
          index.css) le nom garde sa ligne et les boutons passent dessous, au
          lieu de pousser le nom hors de vue. */}
      <div
        className="page-header"
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Space align="center" size={12} style={{ minWidth: 0 }}>
          <Avatar
            size={48} shape="square" src={student.photo_url || undefined} icon={<UserOutlined />}
          />
          <div style={{ minWidth: 0 }}>
            <Typography.Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: "-0.01em" }}>
              {student.last_name} {student.first_name}
            </Typography.Title>
            <Tag
              bordered={false}
              color={student.status === "ACTIF" ? "green" : "default"}
              style={{ marginTop: 2 }}
            >
              {student.status}
            </Tag>
          </div>
        </Space>
        {canEdit && (
          <Space size={8}>
            <Button onClick={() => setEditModalOpen(true)}>Modifier</Button>
            {student.status === "ACTIF" ? (
              <Popconfirm title="Archiver cet élève ?" onConfirm={() => api.archiveStudent(studentId).then(load)}>
                <Button danger>Archiver</Button>
              </Popconfirm>
            ) : (
              <Button onClick={() => api.restoreStudent(studentId).then(load)}>Restaurer</Button>
            )}
          </Space>
        )}
      </div>

      {/* Synthèse : classe, absences du mois, moyenne, reste dû — l'essentiel
          d'un dossier sans avoir à ouvrir chaque onglet. Chaque pastille reste
          muette quand la donnée n'existe pas, plutôt que d'afficher un zéro
          trompeur. */}
      <Space wrap size={8} style={{ marginBottom: 16 }}>
        {profile.enrollments[0]?.class_label && (
          <Tag bordered={false}>Classe {profile.enrollments[0].class_label}</Tag>
        )}
        <Tag bordered={false} color={absencesThisMonth > 0 ? "red" : "default"}>
          {absencesThisMonth} absence{absencesThisMonth > 1 ? "s" : ""} ce mois
        </Tag>
        {lastAverage != null && (
          <Tag bordered={false} color="blue">Moyenne {lastAverage.toFixed(2)} / 20</Tag>
        )}
        {payments != null && outstanding > 0 && (
          <Tag bordered={false} color="orange">
            Reste dû {new Intl.NumberFormat("fr-MA").format(outstanding)} DH
          </Tag>
        )}
        {payments != null && outstanding === 0 && payments.length > 0 && (
          <Tag bordered={false} color="green">À jour de paiement</Tag>
        )}
      </Space>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: "identite",
            label: "Identité",
            children: (
              <Card>
                <Descriptions column={{ xs: 1, md: 3 }} bordered size="small">
                  <Descriptions.Item label="Code Massar">{student.massar_code || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Nom (ar)">{student.last_name_ar || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Prénom (ar)">{student.first_name_ar || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Sexe">{student.gender || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Naissance">
                    {student.birth_date ? dayjs(student.birth_date).format("DD/MM/YYYY") : "—"}
                    {student.birth_place ? ` à ${student.birth_place}` : ""}
                  </Descriptions.Item>
                  <Descriptions.Item label="CIN">{student.cin || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Adresse" span={2}>{student.address || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Ville">{student.city || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Téléphone">{student.phone || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Email">{student.email || "—"}</Descriptions.Item>
                  {profile.can_view_sensitive && (
                    <>
                      <Descriptions.Item label="Groupe sanguin">{student.blood_type || "—"}</Descriptions.Item>
                      <Descriptions.Item label="Allergies" span={2}>{student.allergies || "—"}</Descriptions.Item>
                      <Descriptions.Item label="Notes médicales" span={3}>{student.medical_notes || "—"}</Descriptions.Item>
                    </>
                  )}
                </Descriptions>
              </Card>
            ),
          },
          {
            key: "tuteurs",
            label: `Tuteurs (${profile.guardians.length})`,
            children: (
              <Card
                extra={canEdit && (
                  <Button icon={<PlusOutlined />} onClick={() => setGuardianModalOpen(true)}>
                    Ajouter
                  </Button>
                )}
              >
                <List
                  dataSource={profile.guardians}
                  locale={{ emptyText: "Aucun tuteur" }}
                  renderItem={(g) => (
                    <List.Item
                      // Un parent rattaché dans Tilmicode n'a pas de fiche de
                      // l'école : rien à modifier ni à retirer ici.
                      actions={!canEdit || g.source === "tilmicode" ? [] : [
                        <Button key="edit" type="text" onClick={() => setEditingGuardian(g)}>
                          Modifier
                        </Button>,
                        <Popconfirm
                          key="unlink"
                          title="Retirer ce tuteur ?"
                          onConfirm={() => api.unlinkGuardian(studentId, g.id).then(load)}
                        >
                          <Button type="text" danger>Retirer</Button>
                        </Popconfirm>,
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <Space>
                            {g.last_name} {g.first_name}
                            <Tag>{g.relation}</Tag>
                            {g.is_primary && <Tag color="blue">Contact principal</Tag>}
                            {g.source === "tilmicode" && <Tag color="purple">Compte Tilmicode</Tag>}
                          </Space>
                        }
                        description={g.source === "tilmicode"
                          ? `Parent associé dans Tilmicode${g.email ? ` · ${g.email}` : ""}`
                          : `${WHATSAPP_ENABLED ? "WhatsApp" : "Tél."} : ${g.phone}${g.profession ? ` · ${g.profession}` : ""}${g.email ? ` · ${g.email}` : ""}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>
            ),
          },
          {
            key: "scolarite",
            label: `Scolarité (${profile.enrollments.length})`,
            children: (
              <Card
                extra={canEdit && (
                  <Button icon={<PlusOutlined />} onClick={() => setEnrollModalOpen(true)}>
                    Nouvelle inscription
                  </Button>
                )}
              >
                <ResponsiveTable<Enrollment>
                  rowKey="id"
                  dataSource={profile.enrollments}
                  pagination={false}
                  card={{
                    title: (e) => e.year_label,
                    extra: (e) => <Tag color={enrollmentStatusColor[e.status]}>{e.status}</Tag>,
                    description: (e) => (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {e.class_label || "Non affecté"}
                        {e.level_name ? ` · ${e.level_name}` : ""} · {e.origin}
                      </Typography.Text>
                    ),
                    // Cinq actions ne tiennent pas en pied de carte : seules
                    // celles qui font avancer le dossier y restent, les
                    // impressions se font depuis un poste.
                    actions: (e) => [
                      canEdit && e.status === "EN_ATTENTE" && (
                        <Button key="val" size="small" type="text" onClick={() => api.validateEnrollment(e.id).then(load)}>
                          Valider
                        </Button>
                      ),
                      canEdit && e.status !== "ANNULEE" && (
                        <Button key="cls" size="small" type="text" onClick={() => setClassEnrollment(e)}>
                          {e.class_id ? "Changer de classe" : "Affecter"}
                        </Button>
                      ),
                      canEdit && e.status !== "ANNULEE" && (
                        <Popconfirm key="cancel" title="Annuler cette inscription ?" onConfirm={() => api.cancelEnrollment(e.id).then(load)}>
                          <Button size="small" type="text" danger>Annuler</Button>
                        </Popconfirm>
                      ),
                    ],
                  }}
                  columns={[
                    { title: "Année", dataIndex: "year_label" },
                    { title: "Classe", dataIndex: "class_label", render: (v) => v || "Non affecté" },
                    { title: "Niveau", dataIndex: "level_name", render: (v) => v || "—" },
                    { title: "Origine", dataIndex: "origin" },
                    {
                      title: "Statut",
                      dataIndex: "status",
                      render: (v: string) => <Tag color={enrollmentStatusColor[v]}>{v}</Tag>,
                    },
                    {
                      title: "Actions",
                      render: (_, e) => (
                        <Space wrap>
                          {canEdit && e.status === "EN_ATTENTE" && (
                            <Button size="small" type="primary" onClick={() => api.validateEnrollment(e.id).then(load)}>
                              Valider
                            </Button>
                          )}
                          {/* Une inscription annulée ne se réaffecte pas. */}
                          {canEdit && e.status !== "ANNULEE" && (
                            <Button size="small" onClick={() => setClassEnrollment(e)}>
                              {e.class_id ? "Changer de classe" : "Affecter une classe"}
                            </Button>
                          )}
                          {canEdit && e.status !== "ANNULEE" && (
                            <Popconfirm title="Annuler cette inscription ?" onConfirm={() => api.cancelEnrollment(e.id).then(load)}>
                              <Button size="small" danger>Annuler</Button>
                            </Popconfirm>
                          )}
                          {e.status === "VALIDEE" && (
                            <Button size="small" icon={<PrinterOutlined />} onClick={() => printCertificate(e, "CERTIFICAT_SCOLARITE")}>
                              Certificat
                            </Button>
                          )}
                          {e.status !== "ANNULEE" && (
                            <Button size="small" icon={<PrinterOutlined />} onClick={() => printCertificate(e, "ATTESTATION_INSCRIPTION")}>
                              Attestation
                            </Button>
                          )}
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: "assiduite",
            label: `Assiduité (${attendance.length})`,
            children: (
              <Card>
                <ResponsiveTable<AttendanceRecord>
                  rowKey="id"
                  dataSource={attendance}
                  pagination={{ pageSize: 15, hideOnSinglePage: true }}
                  size="small"
                  locale={{ emptyText: "Aucune absence ni retard enregistré" }}
                  card={{
                    title: (r) => dayjs(r.event_date).format("DD/MM/YYYY"),
                    extra: (r) => (
                      <Tag color={r.kind === "ABSENCE" ? "red" : "orange"}>
                        {r.kind === "ABSENCE" ? "Absence" : `Retard${r.minutes_late ? ` ${r.minutes_late}′` : ""}`}
                      </Tag>
                    ),
                    description: (r) => (
                      <>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {r.slot_start ? `${r.slot_start} ${r.slot_subject ?? ""}`.trim() : "Journée entière"}
                        </Typography.Text>
                        {r.justified && <Tag color="blue">{r.justification || "Justifié"}</Tag>}
                        {WHATSAPP_ENABLED && r.notified_at && <Tag icon={<WhatsAppOutlined />} color="green">Tuteur notifié</Tag>}
                      </>
                    ),
                    actions: (r) =>
                      r.justified
                        ? []
                        : [
                            <Button key="just" size="small" type="text"
                              onClick={() =>
                                pedagogy
                                  .justifyAttendance(r.id, adminJustification(r.kind))
                                  .then(loadAttendance)
                                  .catch(() => message.error("Échec de la justification"))
                              }>
                              Justifier
                            </Button>,
                          ],
                  }}
                  columns={[
                    {
                      title: "Date",
                      dataIndex: "event_date",
                      width: 120,
                      render: (v: string) => dayjs(v).format("DD/MM/YYYY"),
                    },
                    {
                      title: "Type",
                      dataIndex: "kind",
                      width: 110,
                      render: (v: string, r) => (
                        <Tag color={v === "ABSENCE" ? "red" : "orange"}>
                          {v === "ABSENCE" ? "Absence" : `Retard${r.minutes_late ? ` ${r.minutes_late}′` : ""}`}
                        </Tag>
                      ),
                    },
                    {
                      title: "Séance",
                      render: (_, r) =>
                        r.slot_start ? `${r.slot_start} ${r.slot_subject ?? ""}`.trim() : "Journée entière",
                    },
                    {
                      title: "Justification",
                      render: (_, r) =>
                        r.justified ? (
                          <Tag color="blue">{r.justification || "Justifié"}</Tag>
                        ) : (
                          <Button
                            size="small"
                            onClick={() =>
                              pedagogy
                                .justifyAttendance(r.id, adminJustification(r.kind))
                                .then(loadAttendance)
                                .catch(() => message.error("Échec de la justification"))
                            }
                          >
                            Justifier
                          </Button>
                        ),
                    },
                    ...(WHATSAPP_ENABLED ? [{
                      title: "Tuteur",
                      width: 110,
                      align: "center" as const,
                      render: (_: unknown, r: AttendanceRecord) =>
                        r.notified_at ? (
                          <Tag icon={<WhatsAppOutlined />} color="green">Notifié</Tag>
                        ) : (
                          <Typography.Text type="secondary">—</Typography.Text>
                        ),
                    }] : []),
                  ]}
                />
              </Card>
            ),
          },
          {
            key: "notes",
            label: `Notes (${bulletins.length})`,
            children: (
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                {bulletins.length === 0 && (
                  <Card>
                    <Typography.Text type="secondary">
                      Aucun bulletin — les notes apparaîtront une fois les évaluations saisies.
                    </Typography.Text>
                  </Card>
                )}
                {bulletins.map((b) => (
                  <Card
                    key={b.term_label}
                    title={b.term_label}
                    extra={
                      <Space size={12}>
                        {b.rank != null && b.class_size != null && (
                          <Tag bordered={false}>Rang {b.rank} / {b.class_size}</Tag>
                        )}
                        <Typography.Text strong>
                          {b.overall_average != null ? `${b.overall_average.toFixed(2)} / 20` : "—"}
                        </Typography.Text>
                      </Space>
                    }
                  >
                    <Table
                      rowKey="subject_id"
                      dataSource={b.subjects}
                      pagination={false}
                      size="small"
                      locale={{ emptyText: "Aucune matière évaluée" }}
                      columns={[
                        { title: "Matière", dataIndex: "subject_name" },
                        { title: "Coef", dataIndex: "coefficient", width: 70 },
                        {
                          title: "Moyenne /20",
                          dataIndex: "average",
                          width: 120,
                          render: (v?: number) => (v != null ? v.toFixed(2) : "—"),
                        },
                        { title: "Évaluations", dataIndex: "assessment_count", width: 110 },
                        {
                          title: "Appréciation",
                          dataIndex: "appreciation",
                          render: (v?: string) =>
                            v ? <Typography.Text italic>{v}</Typography.Text> : <Typography.Text type="secondary">—</Typography.Text>,
                        },
                      ]}
                    />
                    {/* Appréciation du conseil de classe — la saisie se fait
                        depuis Pédagogie ; la fiche élève la restitue. */}
                    {b.general_appreciation && (
                      <Alert
                        type="info"
                        style={{ marginTop: 12 }}
                        message="Appréciation générale"
                        description={b.general_appreciation}
                      />
                    )}
                  </Card>
                ))}
              </Space>
            ),
          },
          // Onglet masqué hors direction : l'endpoint répond 403 et payments
          // reste nul — inutile d'exposer un onglet vide.
          ...(payments == null ? [] : [{
            key: "paiements",
            label: "Paiements",
            children: (
              <Card>
                <ResponsiveTable<PaymentRow>
                  rowKey={(p) => String(p.period_id)}
                  dataSource={payments}
                  pagination={false}
                  size="small"
                  locale={{ emptyText: "Aucune période de facturation sur ses années d'inscription" }}
                  card={{
                    title: (p) => p.period_label,
                    extra: (p) =>
                      p.status === "PAYE" ? <Tag color="green">Payé</Tag>
                        : p.status === "IMPAYE" ? <Tag color="red">Impayé</Tag>
                        : <Tag>Non pointé</Tag>,
                    description: (p) => {
                      const amount = p.amount_due ?? p.amount_current;
                      return (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {amount != null ? `${new Intl.NumberFormat("fr-MA").format(amount)} DH` : "Montant non défini"}
                        </Typography.Text>
                      );
                    },
                    actions: (p) =>
                      p.status === "PAYE" && p.enrollment_id
                        ? [
                            <Button key="recu" size="small" type="text" icon={<PrinterOutlined />}
                              onClick={() =>
                                navigate(`/enrollments/${p.enrollment_id}/print?doc_type=RECU_PAIEMENT&period_id=${p.period_id}`)
                              }>
                              Reçu
                            </Button>,
                          ]
                        : [],
                  }}
                  columns={[
                    { title: "Période", dataIndex: "period_label" },
                    {
                      title: "Montant",
                      width: 130,
                      align: "right",
                      render: (_, p) => {
                        const amount = p.amount_due ?? p.amount_current;
                        return amount != null
                          ? `${new Intl.NumberFormat("fr-MA").format(amount)} DH`
                          : <Typography.Text type="secondary">—</Typography.Text>;
                      },
                    },
                    {
                      title: "Statut",
                      width: 130,
                      render: (_, p) =>
                        p.status === "PAYE" ? (
                          <Tag color="green">Payé</Tag>
                        ) : p.status === "IMPAYE" ? (
                          <Tag color="red">Impayé</Tag>
                        ) : (
                          <Tag>Non pointé</Tag>
                        ),
                    },
                    {
                      title: "",
                      width: 110,
                      align: "right",
                      // Le reçu s'édite depuis une inscription, et seulement
                      // pour un règlement encaissé (garde du backend).
                      render: (_, p) =>
                        p.status === "PAYE" && p.enrollment_id ? (
                          <Button
                            size="small"
                            icon={<PrinterOutlined />}
                            onClick={() =>
                              navigate(
                                `/enrollments/${p.enrollment_id}/print?doc_type=RECU_PAIEMENT&period_id=${p.period_id}`
                              )
                            }
                          >
                            Reçu
                          </Button>
                        ) : null,
                    },
                  ]}
                />
              </Card>
            ),
          }]),
          ...(!profile.can_view_sensitive ? [] : [{
            key: "discipline",
            label: `Discipline (${(profile.discipline ?? []).length})`,
            children: (
              <Card
                extra={
                  <Button icon={<PlusOutlined />} onClick={() => setDisciplineModalOpen(true)}>
                    Nouvel incident
                  </Button>
                }
              >
                <ResponsiveTable<DisciplinaryRecord>
                  rowKey="id"
                  dataSource={profile.discipline ?? []}
                  pagination={false}
                  card={{
                    title: (d) => dayjs(d.incident_date).format("DD/MM/YYYY"),
                    extra: (d) => <Tag>{d.record_type}</Tag>,
                    description: (d) => (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {d.description}
                        {d.sanction ? ` · Sanction : ${d.sanction}` : ""}
                      </Typography.Text>
                    ),
                    actions: (d) => [
                      <Popconfirm key="del" title="Supprimer ?" onConfirm={() => api.deleteDiscipline(d.id).then(load)}>
                        <Button type="text" danger size="small">Supprimer</Button>
                      </Popconfirm>,
                    ],
                  }}
                  columns={[
                    {
                      title: "Date",
                      dataIndex: "incident_date",
                      render: (v: string) => dayjs(v).format("DD/MM/YYYY"),
                    },
                    { title: "Type", dataIndex: "record_type", render: (v: string) => <Tag>{v}</Tag> },
                    { title: "Description", dataIndex: "description" },
                    { title: "Sanction", dataIndex: "sanction", render: (v) => v || "—" },
                    {
                      title: "",
                      render: (_, d) => (
                        <Popconfirm title="Supprimer ?" onConfirm={() => api.deleteDiscipline(d.id).then(load)}>
                          <Button type="text" danger size="small">Supprimer</Button>
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          }]),
          {
            key: "documents",
            label: `Pièces jointes (${profile.documents.length})`,
            children: (
              <Card
                extra={canEdit && (
                  <Button icon={<PlusOutlined />} onClick={() => setDocumentModalOpen(true)}>
                    Ajouter
                  </Button>
                )}
              >
                <List
                  dataSource={profile.documents}
                  locale={{ emptyText: "Aucune pièce jointe" }}
                  renderItem={(d) => (
                    <List.Item
                      actions={[
                        <a key="open" href={d.file_url} target="_blank" rel="noreferrer">Ouvrir</a>,
                        ...(canEdit ? [
                          <Popconfirm key="del" title="Supprimer ?" onConfirm={() => api.deleteDocument(d.id).then(load)}>
                            <Button type="text" danger size="small">Supprimer</Button>
                          </Popconfirm>,
                        ] : []),
                      ]}
                    >
                      <List.Item.Meta title={d.label || d.doc_type} description={d.doc_type} />
                    </List.Item>
                  )}
                />
              </Card>
            ),
          },
        ]}
      />

      {/* ── Modale : modification de la fiche ── */}
      <StudentEditModal
        open={editModalOpen}
        student={student}
        canViewSensitive={profile.can_view_sensitive}
        onCancel={() => setEditModalOpen(false)}
        onSubmit={(values) =>
          // La fiche existante d'abord : le backend remplace TOUTES les colonnes
          // qu'il reçoit (dont first_name/last_name, absents de ce formulaire).
          api
            .updateStudent(studentId, {
              ...student,
              ...values,
              birth_date: values.birth_date
                ? dayjs(values.birth_date).format("YYYY-MM-DDT00:00:00[Z]")
                : undefined,
            })
            .then(() => {
              setEditModalOpen(false);
              message.success("Fiche mise à jour");
              load();
            })
            .catch((err) => message.error(err?.response?.data?.error || "Échec de la modification"))
        }
      />

      {/* ── Modale : affectation de classe ── */}
      <ClassChangeModal
        enrollment={classEnrollment}
        listClasses={api.listClasses}
        onCancel={() => setClassEnrollment(null)}
        onSubmit={(classId) =>
          api
            .setEnrollmentClass(classEnrollment!.id, classId)
            .then(() => {
              setClassEnrollment(null);
              message.success(classId ? "Classe affectée" : "Élève retiré de sa classe");
              load();
            })
            .catch((err) => message.error(err?.response?.data?.error || "Échec de l'affectation"))
        }
      />

      {/* ── Modale : nouvelle inscription ── */}
      <EnrollModal
        open={enrollModalOpen}
        years={years}
        classes={classes}
        onYearChange={(yearId) => api.listClasses(yearId).then(setClasses).catch(() => undefined)}
        onCancel={() => setEnrollModalOpen(false)}
        onSubmit={(values) =>
          api
            .createEnrollment({ ...values, student_id: studentId })
            .then(() => {
              setEnrollModalOpen(false);
              load();
            })
            .catch((err) => message.error(err?.response?.data?.error || "Échec de l'inscription"))
        }
      />

      {/* ── Modale : modification d'un tuteur ── */}
      {/* Deux écritures, car les données vivent dans deux tables : l'identité du
          tuteur (school_guardians) via updateGuardian, la relation et le contact
          principal (school_student_guardians) via setGuardianLink. La seconde ne
          part qu'après la première : à l'inverse, un échec sur l'identité aurait
          laissé une relation déjà modifiée. */}
      <Modal
        title={editingGuardian ? `Modifier ${editingGuardian.last_name} ${editingGuardian.first_name}` : "Modifier"}
        open={!!editingGuardian}
        onCancel={() => setEditingGuardian(null)}
        footer={null}
        destroyOnClose
      >
        <Form
          layout="vertical"
          onFinish={({ relation, is_primary, ...identity }) =>
            api
              .updateGuardian(editingGuardian!.id, { ...editingGuardian!, ...identity })
              .then(() => api.setGuardianLink(studentId, editingGuardian!.id, { relation, is_primary }))
              .then(() => {
                setEditingGuardian(null);
                message.success("Tuteur modifié");
                load();
              })
              .catch((err) => message.error(err?.response?.data?.error || "Échec de la modification"))
          }
          initialValues={{
            last_name: editingGuardian?.last_name,
            first_name: editingGuardian?.first_name,
            phone: editingGuardian?.phone,
            email: editingGuardian?.email,
            profession: editingGuardian?.profession,
            relation: editingGuardian?.relation ?? "TUTEUR",
            is_primary: editingGuardian?.is_primary ?? false,
          }}
        >
          <Form.Item name="last_name" label="Nom" rules={[{ required: true, message: "Nom requis" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="first_name" label="Prénom" rules={[{ required: true, message: "Prénom requis" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label={WHATSAPP_ENABLED ? "Téléphone (WhatsApp)" : "Téléphone"} rules={[{ required: true, message: "Téléphone requis" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>
          <Form.Item name="profession" label="Profession">
            <Input />
          </Form.Item>
          <Form.Item name="relation" label="Relation" rules={[{ required: true, message: "Relation requise" }]}>
            <Select options={["PERE", "MERE", "TUTEUR", "AUTRE"].map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="is_primary" label="Contact principal" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit">Enregistrer</Button>
        </Form>
      </Modal>

      {/* ── Modale : nouveau tuteur ── */}
      <Modal
        title="Ajouter un tuteur"
        open={guardianModalOpen}
        onCancel={() => setGuardianModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          layout="vertical"
          onFinish={(values) =>
            api
              .addGuardian(studentId, values)
              .then(() => {
                setGuardianModalOpen(false);
                load();
              })
              .catch((err) => message.error(err?.response?.data?.error || "Échec"))
          }
          initialValues={{ relation: "TUTEUR" }}
        >
          <Form.Item name="last_name" label="Nom" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="first_name" label="Prénom" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label={WHATSAPP_ENABLED ? "Téléphone (WhatsApp)" : "Téléphone"} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="relation" label="Relation" rules={[{ required: true }]}>
            <Select options={["PERE", "MERE", "TUTEUR", "AUTRE"].map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="is_primary" label="Contact principal" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit">Ajouter</Button>
        </Form>
      </Modal>

      {/* ── Modale : incident disciplinaire ── */}
      <Modal
        title="Nouvel incident disciplinaire"
        open={disciplineModalOpen}
        onCancel={() => setDisciplineModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          layout="vertical"
          onFinish={(values) =>
            api
              .createDiscipline(studentId, {
                ...values,
                incident_date: dayjs(values.incident_date).format("YYYY-MM-DDT00:00:00[Z]"),
              })
              .then(() => {
                setDisciplineModalOpen(false);
                load();
              })
              .catch((err) => message.error(err?.response?.data?.error || "Échec"))
          }
          initialValues={{ record_type: "AVERTISSEMENT", incident_date: dayjs() }}
        >
          <Form.Item name="incident_date" label="Date de l'incident" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="record_type" label="Type" rules={[{ required: true }]}>
            <Select
              options={["AVERTISSEMENT", "BLAME", "EXCLUSION_TEMPORAIRE", "EXCLUSION_DEFINITIVE", "AUTRE"].map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="sanction" label="Sanction (optionnel)">
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit">Enregistrer</Button>
        </Form>
      </Modal>

      {/* ── Modale : pièce jointe ── */}
      <DocumentAddModal
        open={documentModalOpen}
        onCancel={() => setDocumentModalOpen(false)}
        onSubmit={(values) =>
          api
            .addDocument(studentId, values)
            .then(() => {
              setDocumentModalOpen(false);
              load();
            })
            .catch((err) => message.error(err?.response?.data?.error || "Échec"))
        }
      />
    </div>
  );
};

/**
 * DocumentAddModal — joint une pièce au dossier.
 *
 * Deux appels distincts se cachent derrière cet écran : la SÉLECTION du fichier
 * l'envoie au stockage (DocumentUpload → tc_upload) et n'obtient qu'une URL, et
 * l'ENREGISTREMENT seul rattache cette URL à l'élève. Une version précédente
 * annonçait « Fichier joint » avec une coche verte dès la première étape, en
 * plaçant l'action d'enregistrement en bas du corps du formulaire, hors du pied
 * de modale : on refermait la fenêtre en croyant avoir terminé, et le fichier
 * restait orphelin dans le stockage sans jamais apparaître dans le dossier.
 *
 * D'où le parti pris ici : l'action est dans le pied de modale, à sa place
 * habituelle, et tant que la pièce n'est pas enregistrée un bandeau le dit.
 */
const DocumentAddModal: React.FC<{
  open: boolean;
  onCancel: () => void;
  onSubmit: (values: { doc_type: string; label?: string; file_url: string }) => Promise<unknown> | void;
}> = ({ open, onCancel, onSubmit }) => {
  const [form] = Form.useForm();
  // Le pied de modale vit hors du <Form> : il lui faut son propre reflet de
  // file_url pour savoir s'il peut déclencher l'enregistrement.
  const [fileUrl, setFileUrl] = useState<string>();
  const [saving, setSaving] = useState(false);

  return (
    <Modal
      title="Ajouter une pièce jointe"
      open={open}
      onCancel={onCancel}
      destroyOnClose
      afterClose={() => {
        form.resetFields();
        setFileUrl(undefined);
      }}
      okText="Enregistrer la pièce jointe"
      cancelText="Annuler"
      confirmLoading={saving}
      okButtonProps={{ disabled: !fileUrl }}
      onOk={() => form.submit()}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={(_, all) => setFileUrl(all.file_url)}
        onFinish={(values) => {
          setSaving(true);
          Promise.resolve(onSubmit(values)).finally(() => setSaving(false));
        }}
        initialValues={{ doc_type: "ACTE_NAISSANCE" }}
      >
        <Form.Item name="doc_type" label="Type de document" rules={[{ required: true }]}>
          <Select
            options={["ACTE_NAISSANCE", "PHOTO", "BULLETIN_ANTERIEUR", "CERTIFICAT_MEDICAL", "AUTRE"].map((v) => ({
              value: v,
              label: v,
            }))}
          />
        </Form.Item>
        <Form.Item name="label" label="Libellé">
          <Input placeholder="Optionnel — le nom du fichier par défaut" />
        </Form.Item>
        <Form.Item
          name="file_url"
          label="Fichier"
          rules={[{ required: true, message: "Joindre un fichier" }]}
        >
          <DocumentUpload
            onFileName={(name) => {
              // Ne pré-remplir que si l'utilisateur n'a rien saisi : sa formulation
              // prime sur le nom de fichier brut.
              if (!form.getFieldValue("label")) form.setFieldValue("label", name);
            }}
          />
        </Form.Item>
        {fileUrl && (
          <Alert
            type="warning"
            showIcon
            message="Fichier envoyé, pas encore rattaché"
            description="Cliquez sur « Enregistrer la pièce jointe » pour l'ajouter au dossier de l'élève."
          />
        )}
      </Form>
    </Modal>
  );
};

// Modale d'inscription : année + classe optionnelle.
/**
 * StudentEditModal — modification des données SCOLAIRES de la fiche (Massar,
 * état civil arabe, coordonnées, médical). Le nom et le prénom en sont
 * volontairement absents : ils appartiennent au compte central tc_identity, et
 * SyncStudents les réécrit depuis personalinfo à chaque affichage de la liste —
 * les éditer ici ne tiendrait pas. Ils se modifient depuis le compte.
 *
 * Les champs médicaux ne sont proposés qu'à la direction ; pour les autres
 * comptes le backend ignore ces colonnes de toute façon (includeMedical=false).
 */
const StudentEditModal: React.FC<{
  open: boolean;
  student: Student;
  canViewSensitive: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => Promise<unknown> | void;
}> = ({ open, student, canViewSensitive, onCancel, onSubmit }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  // photoPending : une photo a été envoyée au stockage mais la fiche n'est pas
  // encore enregistrée. Cf. le commentaire du <Alert> plus bas.
  const [photoPending, setPhotoPending] = useState(false);

  return (
    <Modal
      title={`Modifier — ${student.last_name} ${student.first_name}`}
      open={open}
      onCancel={onCancel}
      width={720}
      destroyOnClose
      afterClose={() => {
        form.resetFields();
        setPhotoPending(false);
      }}
      // L'action vit dans le PIED de modale, pas au bas du formulaire : ce
      // dernier fait une douzaine de champs et défile, si bien que le bouton
      // « Enregistrer » sortait de l'écran dès qu'on regardait la photo, tout
      // en haut. On croyait avoir terminé, et rien n'était écrit.
      okText="Enregistrer"
      cancelText="Annuler"
      confirmLoading={saving}
      onOk={() => form.submit()}
    >
    <Form
      form={form}
      layout="vertical"
      onValuesChange={(changed) => {
        if ("photo_url" in changed) setPhotoPending(true);
      }}
      onFinish={(values) => {
        setSaving(true);
        Promise.resolve(onSubmit(values)).finally(() => setSaving(false));
      }}
      initialValues={{
        ...student,
        birth_date: student.birth_date ? dayjs(student.birth_date) : undefined,
      }}
    >
      {/* La photo s'envoie dès la sélection ; l'URL renseigne le champ, écrit
          avec le reste à l'enregistrement. valuePropName="value" : PhotoUpload
          se pilote comme un contrôle antd (value/onChange). */}
      <Form.Item name="photo_url" label="Photo" valuePropName="value">
        <PhotoUpload />
      </Form.Item>
      {photoPending && (
        // L'aperçu se met à jour dès l'envoi : sans ce rappel, l'écran donne
        // toutes les apparences d'une photo déjà enregistrée.
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Photo envoyée, pas encore enregistrée"
          description="Cliquez sur « Enregistrer » en bas de cette fenêtre pour l'attacher à la fiche."
        />
      )}

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item name="massar_code" label="Code Massar">
            <Input placeholder="G123456789" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="last_name_ar" label="النسب (nom en arabe)">
            <Input dir="rtl" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="first_name_ar" label="الاسم (prénom en arabe)">
            <Input dir="rtl" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="gender" label="Sexe">
            <Select allowClear options={[{ value: "M", label: "M" }, { value: "F", label: "F" }]} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="birth_date" label="Date de naissance">
            <DatePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              // Même borne qu'à l'inscription : pas de naissance dans le futur.
              disabledDate={(d) => d.isAfter(dayjs(), "day")}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="birth_place" label="Lieu de naissance">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="cin" label="CIN">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={16}>
          <Form.Item name="address" label="Adresse">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="city" label="Ville">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="phone" label="Téléphone">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="email" label="Email" rules={[{ type: "email", message: "Email invalide" }]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {canViewSensitive && (
        <>
          <Divider orientation="left">Données médicales</Divider>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="blood_type" label="Groupe sanguin">
                <Select
                  allowClear
                  options={["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((v) => ({ value: v, label: v }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item name="allergies" label="Allergies">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="medical_notes" label="Notes médicales">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

    </Form>
    </Modal>
  );
};

/**
 * ClassChangeModal — affecte une classe à une inscription, ou l'en retire.
 *
 * Charge lui-même les classes de l'année DE L'INSCRIPTION : le backend n'accepte
 * qu'une classe de la même année scolaire (cf. SetClass), proposer les autres
 * ne mènerait qu'à un refus. `enrollment` non nul ouvre la modale.
 */
const ClassChangeModal: React.FC<{
  enrollment: Enrollment | null;
  listClasses: (yearId?: number) => Promise<SchoolClass[]>;
  onCancel: () => void;
  onSubmit: (classId: number | null) => void;
}> = ({ enrollment, listClasses, onCancel, onSubmit }) => {
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [loading, setLoading] = useState(false);
  const [classId, setClassId] = useState<number | null>(null);
  const yearId = enrollment?.school_year_id;

  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    listClasses(yearId)
      .then(setClasses)
      .catch(() => setClasses([]))
      .finally(() => setLoading(false));
  }, [listClasses, yearId]);

  // Repartir de la classe courante à chaque ouverture.
  useEffect(() => {
    setClassId(enrollment?.class_id ?? null);
  }, [enrollment]);

  return (
    <Modal
      title={enrollment ? `Classe — ${enrollment.year_label ?? "année en cours"}` : ""}
      open={!!enrollment}
      onCancel={onCancel}
      onOk={() => onSubmit(classId)}
      okText="Enregistrer"
      cancelText="Annuler"
      destroyOnClose
    >
      <Form layout="vertical">
        <Form.Item
          label="Classe"
          // allowClear retire l'élève de sa classe : le backend accepte
          // explicitement class_id null.
          help="Videz le champ pour retirer l'élève de sa classe."
        >
          <Select
            allowClear
            loading={loading}
            value={classId ?? undefined}
            onChange={(v) => setClassId(v ?? null)}
            placeholder={loading ? "Chargement…" : "Aucune classe"}
            options={classes.map((c) => ({
              value: c.id,
              label: c.level_name ? `${c.label} — ${c.level_name}` : c.label,
            }))}
            notFoundContent={loading ? null : "Aucune classe pour cette année"}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const EnrollModal: React.FC<{
  open: boolean;
  years: SchoolYear[];
  classes: SchoolClass[];
  onYearChange: (yearId: number) => void;
  onCancel: () => void;
  onSubmit: (values: { school_year_id: number; class_id?: number }) => void;
}> = ({ open, years, classes, onYearChange, onCancel, onSubmit }) => (
  <Modal title="Nouvelle inscription" open={open} onCancel={onCancel} footer={null} destroyOnClose>
    <Form layout="vertical" onFinish={onSubmit}>
      <Form.Item name="school_year_id" label="Année scolaire" rules={[{ required: true }]}>
        <Select
          options={years.map((y) => ({ value: y.id, label: y.label }))}
          onChange={(v) => onYearChange(v)}
        />
      </Form.Item>
      <Form.Item name="class_id" label="Classe (optionnel)">
        <Select allowClear options={classes.map((c) => ({ value: c.id, label: c.label }))} />
      </Form.Item>
      <Button type="primary" htmlType="submit">Inscrire</Button>
    </Form>
  </Modal>
);

export default StudentDetailPage;
